import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Fieldset } from '@/components/ui/catalyst/fieldset';
import type {
  ContactHandleSchema,
  CreateDepartmentRecordsChannel,
  MailRequirement,
  PoliceRecordsInquiryMajorVendor,
  UpdateDepartmentRecordsChannel,
} from '@/services/api/generated/policeDepartmentsService';
import { enhancedApi } from '@/services/api/policeDepartmentsService';

import { ChannelTypeBadge } from './ChannelTypeBadge';
import { ChannelTypeSelectorEnhanced } from './ChannelTypeSelectorEnhanced';
import { CompactBasicInfoSection } from './CompactBasicInfoSection';
import { CompactMailingSection } from './CompactMailingSection';
import {
  ChannelFormData,
  FormErrors,
  RecordsChannelFormModalProps,
} from './types';
import { WizardSteps } from './WizardSteps';

export const RecordsChannelFormModal = ({
  departmentId,
  channel,
  onClose,
}: RecordsChannelFormModalProps) => {
  const isEdit = !!channel;

  const [createChannel, { isLoading: isCreating }] =
    enhancedApi.usePoliceRecordsChannelsCreateMutation();
  const [updateChannel, { isLoading: isUpdating }] =
    enhancedApi.usePoliceRecordsChannelsUpdateMutation();

  const mailingInstructions = channel?.mailing_instructions;

  const [formData, setFormData] = useState<ChannelFormData>({
    handleType: channel?.handle?.handle_type || 'WEBSITE',
    instructions: channel?.instructions || '',
    handleValue: channel?.handle?.value || '',
    handleName: channel?.handle?.name || '',
    majorVendor:
      (channel?.major_vendor as PoliceRecordsInquiryMajorVendor) ||
      'not_a_portal',
    addressLine1: mailingInstructions?.address?.address_line1 || '',
    addressLine2: mailingInstructions?.address?.address_line2 || '',
    city: mailingInstructions?.address?.city || '',
    state: mailingInstructions?.address?.province_or_state || '',
    postalCode: mailingInstructions?.address?.postal_or_zip || '',
    payableTo: mailingInstructions?.payable_to || '',
    memoInstructions: mailingInstructions?.memo_instructions || '',
    waitDays:
      mailingInstructions?.wait_before_requesting_days?.toString() || '',
    processingDays:
      mailingInstructions?.estimated_processing_days?.toString() || '',
    specialRequirements: mailingInstructions?.special_requirements || '',
    requiredItems:
      (mailingInstructions?.required_items as MailRequirement[]) || [],
    feeAmount: mailingInstructions?.fee_structure?.estimated_fee_cents
      ? (mailingInstructions.fee_structure.estimated_fee_cents / 100).toString()
      : '',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<FormErrors>({});

  const handleFieldChange = (
    field: keyof ChannelFormData,
    value: string | MailRequirement[],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.instructions.trim()) {
      newErrors.instructions = 'Instructions are required';
    }

    if (formData.handleType === 'MAILING_ADDRESS') {
      if (!formData.addressLine1.trim()) {
        newErrors.addressLine1 = 'Address is required';
      }
      if (!formData.city.trim()) {
        newErrors.city = 'City is required';
      }
      if (!formData.state.trim()) {
        newErrors.state = 'State is required';
      }
      if (!formData.postalCode.trim()) {
        newErrors.postalCode = 'ZIP is required';
      }
      if (!formData.payableTo.trim()) {
        newErrors.payableTo = 'Payable to is required';
      }
    }

    if (formData.feeAmount && Number.isNaN(Number(formData.feeAmount))) {
      newErrors.feeAmount = 'Invalid fee amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const handle: ContactHandleSchema = {
      handle_type: formData.handleType as ContactHandleSchema['handle_type'],
      value: formData.handleValue || null,
      name: formData.handleName || null,
    };

    const mailingInstructionsPayload =
      formData.handleType === 'MAILING_ADDRESS'
        ? {
            instructions: formData.instructions,
            address: {
              address_line1: formData.addressLine1,
              address_line2: formData.addressLine2 || undefined,
              city: formData.city,
              province_or_state: formData.state,
              postal_or_zip: formData.postalCode,
              country_code: 'US',
            },
            payable_to: formData.payableTo || undefined,
            memo_instructions: formData.memoInstructions || undefined,
            wait_before_requesting_days: formData.waitDays
              ? Number(formData.waitDays)
              : undefined,
            estimated_processing_days: formData.processingDays
              ? Number(formData.processingDays)
              : undefined,
            required_items:
              formData.requiredItems.length > 0
                ? formData.requiredItems
                : undefined,
            special_requirements: formData.specialRequirements || undefined,
            fee_structure: formData.feeAmount
              ? {
                  estimated_fee_cents: Math.round(
                    Number(formData.feeAmount) * 100,
                  ),
                  components: undefined,
                  notes: undefined,
                  confidence_score: undefined,
                  reasoning: undefined,
                }
              : undefined,
            citations: undefined,
          }
        : undefined;

    try {
      if (isEdit && channel) {
        const payload: UpdateDepartmentRecordsChannel = {
          id: channel.id,
          instructions: formData.instructions,
          handle,
          fee_amount: undefined,
          major_vendor: formData.majorVendor,
          required_document_url: undefined,
          mailing_instructions: mailingInstructionsPayload,
        };
        await updateChannel({
          departmentId,
          id: channel.id,
          updateDepartmentRecordsChannel: payload,
        }).unwrap();
        toast.success('Records channel updated');
      } else {
        const payload: CreateDepartmentRecordsChannel = {
          police_department_id: departmentId,
          instructions: formData.instructions,
          handle,
          fee_amount: undefined,
          major_vendor: formData.majorVendor,
          required_document_url: undefined,
          mailing_instructions: mailingInstructionsPayload,
        };
        await createChannel({
          departmentId,
          createDepartmentRecordsChannel: payload,
        }).unwrap();
        toast.success('Records channel created');
      }
      onClose();
    } catch (error) {
      const action = isEdit ? 'update' : 'create';
      toast.error(`Failed to ${action} records channel`);
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      setCurrentStep(1);
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      setCurrentStep(0);
    }
  };

  const isMailingAddress = formData.handleType === 'MAILING_ADDRESS';
  const totalSteps = 2;
  const stepLabels = ['Channel Type', 'Details'];

  // Wizard mode for creation
  if (!isEdit) {
    return (
      <Dialog open onClose={onClose} size="3xl">
        <DialogTitle>Add Records Channel</DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogBody>
            <Fieldset>
              <div className="space-y-6">
                {/* Wizard Progress */}
                <WizardSteps
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  stepLabels={stepLabels}
                />

                {/* Step 1: Channel Type Selection */}
                {currentStep === 0 && (
                  <div className="py-4">
                    <ChannelTypeSelectorEnhanced
                      value={formData.handleType}
                      onChange={(value) =>
                        handleFieldChange('handleType', value)
                      }
                    />
                  </div>
                )}

                {/* Step 2: Details */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                      <div>
                        <p className="text-xs text-gray-600">Channel Type</p>
                        <div className="mt-1">
                          <ChannelTypeBadge type={formData.handleType} />
                        </div>
                      </div>
                      <Button
                        plain
                        type="button"
                        onClick={handleBack}
                        className="text-sm"
                      >
                        Change
                      </Button>
                    </div>

                    <CompactBasicInfoSection
                      formData={formData}
                      errors={errors}
                      onChange={handleFieldChange}
                    />

                    {isMailingAddress && (
                      <CompactMailingSection
                        formData={formData}
                        errors={errors}
                        onChange={handleFieldChange}
                      />
                    )}
                  </div>
                )}
              </div>
            </Fieldset>
          </DialogBody>

          <DialogActions>
            <Button plain onClick={onClose} type="button">
              Cancel
            </Button>
            <div className="flex gap-2">
              {currentStep === 1 && (
                <Button plain type="button" onClick={handleBack}>
                  Back
                </Button>
              )}
              {currentStep === 0 ? (
                <Button type="button" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button type="submit" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create Channel'}
                </Button>
              )}
            </div>
          </DialogActions>
        </form>
      </Dialog>
    );
  }

  // Edit mode - single page, streamlined
  return (
    <Dialog open onClose={onClose} size="2xl">
      <DialogTitle>Edit Records Channel</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <div className="space-y-6">
              <div className="rounded-lg bg-gray-50 px-4 py-3">
                <p className="mb-1 text-xs text-gray-600">Channel Type</p>
                <ChannelTypeBadge type={formData.handleType} />
              </div>

              <CompactBasicInfoSection
                formData={formData}
                errors={errors}
                onChange={handleFieldChange}
              />

              {isMailingAddress && (
                <CompactMailingSection
                  formData={formData}
                  errors={errors}
                  onChange={handleFieldChange}
                />
              )}
            </div>
          </Fieldset>
        </DialogBody>

        <DialogActions>
          <Button plain onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Update Channel'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
